import React, { useMemo } from "react";

import { Modal, Button, Alert, Table } from "antd";

import { useTranslation } from "@opendash/core";

// Inhalt einer Zeile so, wie er in der Tabelle sichtbar ist: leere Felder und
// nicht gesetztes "active" sind für den Nutzer nicht von "" bzw. aktiv zu
// unterscheiden und dürfen darum nicht als Widerspruch gelten.
function signature(row) {
  return JSON.stringify({
    owner: row.owner ?? "",
    misc: row.misc ?? "",
    misc2: row.misc2 ?? "",
    active: row.active !== false,
  });
}

export function analyzeDuplicates(transponders) {
  const variantsById = new Map();
  const redundant = new Set();

  transponders.forEach((row, index) => {
    if (row._deleted) {
      return;
    }

    let variants = variantsById.get(row.id);

    if (!variants) {
      variants = new Map();
      variantsById.set(row.id, variants);
    }

    const key = signature(row);

    if (variants.has(key)) {
      redundant.add(index);
    } else {
      variants.set(key, row);
    }
  });

  const conflicts = [];

  for (const [id, variants] of variantsById) {
    if (variants.size > 1) {
      conflicts.push({ id, rows: [...variants.values()] });
    }
  }

  return { redundant, conflicts };
}

export default function GreisTranspondersDedupe({
  open,
  close,
  transponders,
  overwriteTransponders,
}) {
  const t = useTranslation();

  const { redundant, conflicts } = useMemo(
    () => analyzeDuplicates(transponders),
    [transponders]
  );

  const conflictRows = useMemo(
    () =>
      conflicts.flatMap((conflict) =>
        conflict.rows.map((row, index) => ({ ...row, _key: conflict.id + index }))
      ),
    [conflicts]
  );

  const columns = [
    {
      title: t("greis:transponders.admin.cols.id"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("greis:transponders.admin.cols.owner"),
      dataIndex: "owner",
      key: "owner",
    },
    {
      title: t("greis:transponders.admin.cols.misc"),
      dataIndex: "misc",
      key: "misc",
    },
    {
      title: t("greis:transponders.admin.cols.misc2"),
      dataIndex: "misc2",
      key: "misc2",
    },
    {
      title: t("greis:transponders.admin.cols.active"),
      dataIndex: "active",
      key: "active",
      render: (v) =>
        v === false
          ? t("greis:transponders.admin.dedupe_inactive")
          : t("greis:transponders.admin.cols.active"),
    },
  ];

  const style = { marginBottom: 24 };

  return (
    <Modal
      title={t("greis:transponders.admin.dedupe")}
      visible={open}
      onOk={(e) => close()}
      onCancel={(e) => close()}
      width={800}
      footer={[
        <Button key="back" onClick={(e) => close()}>
          {t("greis:transponders.admin.import_action_cancel")}
        </Button>,
        <Button
          key="dedupe_action_remove"
          type="primary"
          disabled={redundant.size === 0}
          onClick={(e) => {
            overwriteTransponders(
              transponders.map((row, index) =>
                redundant.has(index) ? { ...row, _deleted: true } : row
              )
            );

            close();
          }}
          children={t("greis:transponders.admin.dedupe_action_remove")}
        />,
      ]}
    >
      {redundant.size === 0 && conflicts.length === 0 && (
        <Alert
          type="success"
          style={style}
          message={t("greis:transponders.admin.dedupe_none")}
        />
      )}

      {redundant.size > 0 && (
        <Alert
          type="info"
          style={style}
          message={t("greis:transponders.admin.dedupe_exact", {
            count: redundant.size,
          })}
        />
      )}

      {conflicts.length > 0 && (
        <>
          <Alert
            type="warning"
            style={style}
            message={t("greis:transponders.admin.dedupe_conflicts", {
              count: conflicts.length,
            })}
            description={t("greis:transponders.admin.dedupe_conflicts_hint")}
          />

          <Table
            rowKey={(row) => row._key}
            dataSource={conflictRows}
            columns={columns}
            size="small"
            pagination={{ defaultPageSize: 10, showSizeChanger: false }}
          />
        </>
      )}
    </Modal>
  );
}
